// Phase 36 (artboards 21a/21b) — the five tones, stated once.
//
// Every chip, notice, row edge and rail dot in the product resolves to one of
// these. They are here rather than inlined at each call site because the set is
// a vocabulary, not a palette: each tone means one thing, and the meanings are
// what stop a screen turning into decoration.
//
//   ok        met, live, delivered, connected
//   warn      needs attention — not broken yet
//   danger    breached, failed, over by
//   machine   A MACHINE DID THIS, and nothing else
//   neutral   a state with no consequence
//
// The machine tone is the one a reskin must not break. It marks embedding
// state, retrieval scores, citations and assistant output. The moment it
// appears on an author, a manual edit or a publish button, the reader loses
// the only signal that tells them what a person wrote and what a model did.

export type Tone = 'ok' | 'warn' | 'danger' | 'machine' | 'neutral';

export interface ToneStyle {
  /** Fill behind a chip or notice. */
  wash: string;
  /** Border, and the rule on a row edge. */
  line: string;
  /** Label text, and the icon square's fill. */
  ink: string;
}

export const TONE: Record<Tone, ToneStyle> = {
  ok: {
    wash: 'var(--co-green-wash)',
    line: 'var(--co-green-line)',
    ink: 'var(--co-green-ink)',
  },
  warn: {
    wash: 'var(--co-terracotta-wash)',
    line: 'var(--co-terracotta-line)',
    // `--co-terracotta-ink`, not `--co-terracotta`: the mid tone is a border
    // colour and drops under 4.5:1 as 9.5px uppercase text.
    ink: 'var(--co-terracotta-ink)',
  },
  danger: {
    wash: 'var(--co-burgundy-wash)',
    line: 'var(--co-burgundy-line)',
    ink: 'var(--co-burgundy)',
  },
  machine: {
    wash: 'var(--co-machine-wash)',
    line: 'var(--co-machine-line)',
    ink: 'var(--co-machine-ink)',
  },
  neutral: {
    wash: 'var(--co-selected)',
    line: 'var(--co-line-strong)',
    ink: 'var(--co-ink-3)',
  },
};

/**
 * Priority, as a tone.
 *
 * Only the top two carry hue. Four coloured priorities is four signals, which
 * is none: if `low` is also a colour then colour no longer means "look at
 * this", it just means "this row has a priority", which every row does.
 */
export function priorityTone(priority: string | null | undefined): Tone {
  switch (String(priority ?? '').toLowerCase()) {
    case 'urgent':
    case 'critical':
      return 'danger';
    case 'high':
      return 'warn';
    default:
      return 'neutral';
  }
}

/**
 * Should a count be drawn at all?
 *
 * A notice reading `0 articles invisible to the AI` is furniture: it occupies
 * the place a real warning would appear and trains the reader to skip it.
 * Absent is the correct rendering of nothing being wrong.
 */
export function hasFinding(count: number | null | undefined): boolean {
  return typeof count === 'number' && Number.isFinite(count) && count > 0;
}

/** The focus ring. Never coloured — a brass ring reads as a brass control. */
export const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 ' +
  'focus-visible:outline-[var(--co-ink)]';
